<template>
  <div class="relative" id="new_color_picker" :class="$attrs.class">
    <!-- Toolbar button (small) -->
    <button 
      v-if="is_toolbar"
      @click.stop="toggle_picker"
      class="w-8 h-6 border-2 border-gray-500 rounded-sm cursor-pointer hover:border-gray-400"
      :style="{ backgroundColor: color }"
      type="button"
      ref="triggerButton"
    >
    </button>
    
    <!-- Interface button (large with full interface design) -->
    <button 
      v-else
      @click.stop="toggle_picker"
      class="w-full h-10 bg-gray-800/50 border border-gray-600/30 rounded-lg cursor-pointer"
      :style="{ backgroundColor: color }"
      type="button"
      ref="triggerButton"
    >
    </button>
  </div>
  <!-- 
        v-model	string	default ''
        mode	string	solid or gradient default gradient
        theme	string	light or dark default light
        type	string	Return colour value type HEX8 RGBA RGB HEX default HEX8
        inputType	string	Input type of colour values RGB HSL HSV CMYK default RGB
        disabled	boolean	default false
        local	object	default { angle: '', positionX: '', positionY: '', solid: '', gradient: '', linear: '', radial: '', colorPalette: '', btnSaveLabel:'Save', btnCancelLabel:'Cancel'  }
        colorListCount	number	Determines how many colours to keep in the colour list default 18
        showColorList	boolean	default true
        showEyeDrop	boolean	default true Supports Chrome and Edge only
        showAlpha	boolean	default true
        showInputMenu	boolean	default true
        showInputSet	boolean	default true
        showPickerMode	boolean	default true
        iconClasses	object	default { ruler: '', eyeDroper: '', arrowDown: '', save: '', delete: '' }
        gradientMode	linear or radial	default linear Sets the initial gradient mode when the picker is started
        showButtons	boolean	default true    
    -->
    
    <!-- Floating color picker - Teleported to body -->
  <Teleport to="body">
    <div v-if="show_picker" class="fixed z-[2000]" :style="picker_position_style" ref="colorPickerContainer" id="color_picker_container" @click.stop @mousedown.stop>
      <Vue3ColorPicker 
        v-model="color" 
        mode="solid"
        theme="light"
        type="HEX8" 
        inputType="RGB"
        :showColorList="true"
        :showEyeDrop="true" 
        :showInputMenu="false"
        :showInputSet="true"
        :showPickerMode="false"
        :showAlpha="show_alpha"
        
        @update:model-value="on_color_change"
      />
    </div>
  </Teleport>
</template>

<style scoped>
/* No custom CSS needed - using only Tailwind */
</style>

<script>
import { Vue3ColorPicker } from '@cyhnkckali/vue3-color-picker';

export default {
  name: 'ColorPickerWrapper',
  inheritAttrs: false,
  components: {
    Vue3ColorPicker
  },
  props: {
    modelValue: {
      type: String,
      default: '#ffffff'
    },
    forFill: {
      type: Boolean,
      default: true
    },
    is_toolbar: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      show_picker: false,
      color: this.modelValue || '#ffffff',
      change_timer: null,
      picker_position: {
        top: '0px',
        left: '0px'
      }
    };
  },
  computed: {
    is_balloon_selected() {
      // Check if parent has a selected object and it's a balloon
      return this.$parent && 
             this.$parent.selected_object && 
             this.$parent.selected_object.getAttr && 
             this.$parent.selected_object.getAttr('object_type') === 'balloon';
    },
    
    show_alpha() {
      // Only show alpha for balloons and only for fill (not stroke)
      return this.is_balloon_selected && this.forFill;
    },
    
    current_alpha() {
      // Only return alpha for balloons, default to 1 for other objects
      if (this.show_alpha && this.$parent.balloon_fill_alpha !== undefined) {
        return this.$parent.balloon_fill_alpha;
      }
      return 1;
    },
    
    picker_position_style() {
      return {
        top: this.picker_position.top,
        left: this.picker_position.left
      };
    }
  },
  watch: {
    modelValue(newVal) {
      // Only update color when picker is active
      if (!this.show_picker) return;
      this.color = this.get_initial_color();
    },

    // Watch for changes in balloon alpha to update the color picker
    '$parent.balloon_fill_alpha'() {
      if (this.show_alpha && this.show_picker) {
        this.color = this.get_initial_color();
      }
    }
  },
  mounted() {
    // Initialize color with proper alpha on mount
    this.color = this.get_initial_color();
    
    // Listen for global close event only
    document.addEventListener('close-color-pickers', this.close_picker);
  },
  beforeUnmount() {
    // Clean up timer to prevent memory leaks
    if (this.change_timer) {
      clearTimeout(this.change_timer);
      this.change_timer = null;
    }
    
    // Remove global event listener
    document.removeEventListener('close-color-pickers', this.close_picker);
  },
  methods: {
    get_initial_color() {
      let base_color = this.modelValue || '#ffffff';
      
      // Check if this is for fill, balloon is selected, and we have alpha available
      const is_balloon_selected = this.$parent && 
                                  this.$parent.selected_object && 
                                  this.$parent.selected_object.getAttr && 
                                  this.$parent.selected_object.getAttr('object_type') === 'balloon';
      const should_show_alpha = is_balloon_selected && this.forFill;
      
      // If balloon is selected, has alpha, and this is for fill, construct HEX8 format
      if (should_show_alpha && this.$parent?.balloon_fill_alpha !== undefined) {
        // Ensure base_color is 6 digits
        if (base_color.length === 9) {
          base_color = base_color.substring(0, 7);
        }
        // Convert alpha (0-1) to hex (00-FF)
        const alpha_hex = Math.round(this.$parent.balloon_fill_alpha * 255).toString(16).padStart(2, '0');
        return base_color + alpha_hex;
      }
      
      return base_color;
    },
    
    toggle_picker(event) {
      if (this.show_picker) {
        this.show_picker = false;
      } else {
        this.show_picker = true;
        // Calculate position after picker is shown
        this.$nextTick(() => {
          this.calculate_picker_position();
        });
      }
    },
    
    calculate_picker_position() {
      if (!this.$refs.triggerButton) return;
      
      const button = this.$refs.triggerButton;
      
      // Get button position relative to viewport
      const button_rect = button.getBoundingClientRect();
      
      // Color picker approximate dimensions
      const picker_width = 280;
      const picker_height = 400;
      
      // Get viewport dimensions
      const viewport_width = window.innerWidth;
      const viewport_height = window.innerHeight;
      
      // Calculate optimal position
      let left = button_rect.left;
      let top = button_rect.bottom + 5; // 5px spacing below button
      
      // Adjust horizontal position if picker would overflow right edge
      if (left + picker_width > viewport_width) {
        left = button_rect.right - picker_width;
      }
      
      // Ensure picker doesn't go off left edge
      if (left < 10) {
        left = 10;
      }
      
      // Adjust vertical position if picker would overflow bottom edge
      if (top + picker_height > viewport_height) {
        top = button_rect.top - picker_height - 5; // Show above button
      }
      
      // Ensure picker doesn't go off top edge
      if (top < 10) {
        top = 10;
      }
      
      this.picker_position = {
        top: `${top}px`,
        left: `${left}px`
      };
    },
    
    close_picker() {
      this.show_picker = false;
      
      // Only emit change when picker was actually open and user finished selecting
      if (this.change_timer) {
        clearTimeout(this.change_timer);
        this.change_timer = null;
        this.$emit('change');
      }
    },

    on_color_change(color) {
      // Only emit events when picker is actively being used
      if (!this.show_picker) return;
      
      let hex_color = color;
      
      // If color is HEX8 format (#rrggbbaa), extract color and alpha
      if (color && color.length === 9 && color.startsWith('#')) {
        // Extract 6-digit hex color (without alpha)
        hex_color = color.substring(0, 7);
        // Extract alpha from last 2 characters and convert to 0-1 range
        const alpha_hex = color.substring(7, 9);
        const alpha_value = parseInt(alpha_hex, 16) / 255;
        
        // Update balloon alpha only if this is for fill and we should show alpha
        if (this.show_alpha && this.$parent.balloon_fill_alpha !== undefined) {
          this.$parent.balloon_fill_alpha = alpha_value;
          // Trigger the same update logic as the app's fill opacity slider
          if (this.$parent.on_balloon_fill_alpha_input) {
            this.$parent.on_balloon_fill_alpha_input();
          }
        }
      }
      // For HEX6 format, preserve current alpha by reconstructing HEX8 if needed
      else if (this.show_alpha && this.$parent.balloon_fill_alpha !== undefined) {
        // Reconstruct the color with current alpha for internal storage
        const current_alpha = this.$parent.balloon_fill_alpha;
        const alpha_hex = Math.round(current_alpha * 255).toString(16).padStart(2, '0');
        this.color = hex_color + alpha_hex;
      } else {
        // For non-fill or non-balloon cases, just use the color as-is
        this.color = hex_color;
      }
      
      // Always emit just the 6-digit hex color
      this.$emit('update:modelValue', hex_color);
      
      // Debounce the change event to avoid flooding the history
      if (this.change_timer) {
        clearTimeout(this.change_timer);
      }
      this.change_timer = setTimeout(() => {
        this.$emit('change');
        this.change_timer = null;
      }, 500); // 500ms delay like the original implementation
    },
    
    on_window_resize() {
      if (this.show_picker) {
        this.$nextTick(() => {
          this.calculate_picker_position();
        });
      }
    },
    
    on_window_scroll() {
      if (this.show_picker) {
        this.$nextTick(() => {
          this.calculate_picker_position();
        });
      }
    }
    
  }
};
</script>
